import { Duration } from 'luxon';
import { useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import CopyableField from 'dashboard/components/CopyableField';
import FormSendInquiryEmail, {
  SendInquiryEmailFormData,
} from 'dashboard/forms/FormSendInquiryEmail';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { Environment } from 'dashboard/models/environment';
import { Inquiry } from 'dashboard/models/inquiry';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { getRootDomain } from 'lib/utils/url';

const CopyableFieldWrapper = styled('a')`
  white-space: nowrap;

  input {
    color: ${({ theme }) => theme.color.text.brand};
    cursor: pointer;
  }
`;

interface PostInquiryCreateModalProps {
  createdInquiry: Inquiry;
  subdomain: string | null;
  customDomain: string | null;
  emailAddressPrefill?: string;
  sendCreatedInquiryEmailSelected: boolean;
  currentEnvironment: Environment;
  handleSendInquiryEmail: (args: SendInquiryEmailFormData) => Promise<void>;
  handleClose: () => void;
}

const PostInquiryCreateModal = ({
  createdInquiry,
  subdomain,
  customDomain,
  emailAddressPrefill,
  sendCreatedInquiryEmailSelected,
  currentEnvironment,
  handleSendInquiryEmail,
  handleClose,
}: PostInquiryCreateModalProps) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/dashboard/inquiries/${createdInquiry.id}`);
  };

  let link;
  if (customDomain) {
    link = `${location.protocol}//${customDomain}/verify?inquiry-id=${createdInquiry.id}`;
  } else {
    link = `${location.protocol}//${[subdomain, getRootDomain(location.host)]
      .filter(Boolean)
      .join('.')}/verify?inquiry-id=${createdInquiry.id}`;
  }

  if (!sendCreatedInquiryEmailSelected) {
    return (
      <ModalContainer>
        <ModalHeader onClose={handleClose}>View new Inquiry</ModalHeader>
        <ModalBody>
          <CopyableFieldWrapper href={link} target='_blank'>
            <CopyableField value={link} />
          </CopyableFieldWrapper>
          <Spacer height={3} />
          <HStack>
            <Button variant='basic' onClick={handleClose}>
              Close
            </Button>
            <Button onClick={handleClick}>View Inquiry</Button>
          </HStack>
        </ModalBody>
      </ModalContainer>
    );
  } else {
    return (
      <ModalContainer>
        <ModalHeader onClose={handleClose}>Email Inquiry link</ModalHeader>
        <ModalBody>
          <FormSendInquiryEmail
            handleCancel={handleClose}
            onSubmit={handleSendInquiryEmail}
            currentEnvironment={currentEnvironment}
            initialValues={{
              'email-address': emailAddressPrefill,
              'email-subject': 'Verify your identity',
              'email-greeting': 'Please verify your identity',
              'email-body': `Click the link below and complete the identity verification process within ${Duration.fromObject(
                {
                  seconds:
                    createdInquiry.attributes['expiration-after-create-interval-seconds'] ?? 86400,
                },
              )
                .rescale()
                .toHuman()}.`,
              'email-cta': 'Verify identity',
            }}
          />
        </ModalBody>
      </ModalContainer>
    );
  }
};

export default PostInquiryCreateModal;
